import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	ChangeSet,
	ChangeSetEntityChangeType,
	DataTypeState,
	EntityId,
	EntityType,
	ProjectState,
	resolveEntityName,
	ValueDescriptorState,
	ArgumentDeclarationState,
	READABLE_ENTITY_TYPES,
	EntityInstanceErrorCode,
	IValueDescriptorShallowTransfer,
	IPropertyShallowTransfer,
	PropertyState,
	StateMutationErrorCode,
	translateLogicErrorCodeShort,
	toSentenceCase,
	getRecursiveParentsIds,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import {
	Alert,
	Autocomplete,
	TextField,
	Tooltip as MuiTooltip,
	Box,
	Checkbox,
	Chip,
	Stack,
	Collapse,
} from '@mui/material';
import CheckboxWithLabel from '../../../lib/checkbox/CheckboxWithLabel';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { EditorService } from '../../../services/editor';
import DataTypeBuilder, {
	AllowedDataTypeListTypes,
	ILiteralValueOption,
} from '../../data-type/DataTypeBuilder';
import Tooltip from '../../../lib/tooltip/Tooltip';
import Button from '../../../lib/button/Button';
import { useParams } from 'react-router-dom';
import EntityIcon from '../../entity-icon/EntityIcon';
import EntityDialogSectionHeader from '../../EntityDialogSectionHeader';
import DynamicValueEntry from '../../dynamic-value/DynamicValueEntry';
import WarningIcon from '../../../assets/warning-sign-24px.png';
import { DataTypeBuilderOption } from '../../../services/editor/utils';
import { debounce } from '../../../utils';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

export interface IArgumentDeclarationProps {
	project: EditorService;
	entity?: ArgumentDeclarationState;
	onClose?: (e: any) => void;
}

export enum ArgumentDeclarationErrorEnum {
	NameRequired = 'name-required',
	IncompleteDataType = 'incomplete-data-type',
}

export type ArgumentDeclarationError =
	| ArgumentDeclarationErrorEnum
	| EntityInstanceErrorCode;

export function fromArgumentDeclarationErrorToReadableMessage(
	error: ArgumentDeclarationError | string,
): string {
	switch (error) {
		case ArgumentDeclarationErrorEnum.NameRequired:
			return 'Name is required';
		case ArgumentDeclarationErrorEnum.IncompleteDataType:
			return 'The composition of the data type is incomplete. Please fill in all required fields on each data type element.';
		default:
			return toSentenceCase(error);
	}
}

function calculateArgumentDeclarationCreationFormErrors(
	entity: ArgumentDeclarationState,
): (ArgumentDeclarationError | string)[] {
	const errors: (ArgumentDeclarationError | string)[] = [];

	if (!entity.name) {
		errors.push(ArgumentDeclarationErrorEnum.NameRequired);
	}

	if (!!entity.dataType) {
		if (!entity.dataType.isResolved) {
			errors.push(ArgumentDeclarationErrorEnum.IncompleteDataType);
		}

		const flatErrors = (
			entity.dataType.toFlat().filter((child) => {
				if (child.type === EntityType.DataType) {
					if (child.id === entity.dataType.id) {
						// This is the root data type, skip
						return true;
					}

					const allParentsOfNestedDT = getRecursiveParentsIds(child);

					if (allParentsOfNestedDT.includes(entity.dataType.id)) {
						// This is a nested data type that creates a circular reference
						return true;
					} else {
						return false;
					}
				}

				return false;
			}) as DataTypeState[]
		).flatMap((dt) => dt.getShallowErrors());

		if (flatErrors.length > 0) {
			errors.push(
				...flatErrors.map((err) => translateLogicErrorCodeShort(err)),
			);
		}
	}

	return errors;
}

// Not all entity types can have inputs with any data type
// Sometimes, like in the case of a search, the data type of its inputs can only be a primitive type or a list of primitives
// This function, given an option, will return some metadata that will help it render the option in the data type builder
export function resolveDataTypeOptionConfigForArgumentDeclaration(
	option: DataTypeBuilderOption | ILiteralValueOption,
	input: ArgumentDeclarationState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsTypeConfigForArgumentDeclaration(
	input: ArgumentDeclarationState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeLikeConfigForArgumentDeclaration(
	input: ArgumentDeclarationState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsListConfigForArgumentDeclaration(
	input: ArgumentDeclarationState,
): {
	allowed: AllowedDataTypeListTypes;
	tooltip: React.ReactNode;
} {
	return {
		allowed: AllowedDataTypeListTypes.All,
		tooltip: null,
	};
}

const ArgumentDeclaration: React.FC<IArgumentDeclarationProps> = ({
	project,
	entity: _entity,
	onClose,
}) => {
	const [updateKey, setUpdateKey] = useState(0);
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: ArgumentDeclarationState | null =
		_entity ||
		(project?.logic?.get(entityId) as ArgumentDeclarationState | null) ||
		null;

	const [dataType, _setDataType] = useState<DataTypeState | null>(
		entity?.dataType || null,
	);
	const [entityName, setEntityName] = useState<string>(entity.name);
	const [entityDescription, setEntityDescription] = useState<string>(
		entity.description,
	);

	const [implementsValue, setImplementsValue] = useState<
		(IPropertyShallowTransfer | IValueDescriptorShallowTransfer)[]
	>(entity.implements.map((ent) => ent.toShallowJSON()));

	const [isConstant, _setIsConstant] = useState<boolean>(
		(entity as ArgumentDeclarationState).constant || false,
	);
	const [isRequired, _setIsRequired] = useState<boolean>(entity.required);

	const [metadataSectionOpen, setMetadataSectionOpen] = useState(true);
	const [dataTypeSectionOpen, setDataTypeSectionOpen] = useState(false);
	const [defaultValueSectionOpen, setDefaultValueSectionOpen] = useState(false);
	const [baseFunctionalitySectionOpen, setBaseFunctionalitySectionOpen] =
		useState(false);
	const [interactivitySectionOpen, setInteractivitySectionOpen] =
		useState(false);

	const setIsRequired = useCallback((value: boolean) => {
		_setIsRequired(value);

		const changeSet = project?.logic?.addChangeSet(
			new ChangeSet(
				project?.logic,
				ProjectState.sessionAuthor,
				new Date().toISOString(),
				entity,
				false,
				value
					? StateMutationAction.MakeArgRequired
					: StateMutationAction.MakeArgNonRequired,
			),
		);

		entity?.metaSync({ required: value }, changeSet);

		entity?.afterAllChildrenInitialized(changeSet);

		project.renderAndCloseChangeSet(changeSet);
	}, []);

	const debouncedOnChangeName = useCallback(
		debounce(
			(value: string) => {
				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						StateMutationAction.RenameArg,
					),
				);

				setUpdateKey((prev) => prev + 1);
				entity?.metaSync({ name: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			1500,
			false,
		),
		[],
	);

	const debouncedOnChangeDescription = useCallback(
		debounce(
			(value: string) => {
				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						StateMutationAction.ChangeArgDescription,
					),
				);

				setUpdateKey((prev) => prev + 1);
				entity?.metaSync({ description: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			600,
			false,
		),
		[],
	);

	const [errors, setErrors] = useState<(ArgumentDeclarationError | string)[]>(
		calculateArgumentDeclarationCreationFormErrors(entity),
	);

	const setDataType = (
		newDataTypeEntity: DataTypeState | null,
		changeSet: ChangeSet | null,
	) => {
		newDataTypeEntity?.setParent(entity, changeSet);
		entity?.setDataType(newDataTypeEntity, changeSet);

		changeSet?.add(entity, ChangeSetEntityChangeType.Affected);

		_setDataType(newDataTypeEntity);
		setUpdateKey((prev) => prev + 1);

		project.renderAndCloseChangeSet(changeSet);
	};

	useEffect(() => {
		const newErrors = calculateArgumentDeclarationCreationFormErrors(entity);
		setErrors(newErrors);
		project.emit(entity.parent.id, {});
	}, [entityName, updateKey]);

	const implementsOptions = useMemo(() => {
		const options: (ValueDescriptorState | PropertyState)[] = [
			...(entity.parent.implements?.inputs || []),
		];

		// There might be a case where the current implementations are not in the options
		// We then add its to the options so it can be shown as selected
		const activeImplementationsNotInOptions = entity.implements.filter(
			(ent) => !options.find((o) => o.id === ent.id),
		);

		options.push(...activeImplementationsNotInOptions);

		return options.map((ent) => ent.toShallowJSON());
	}, [entity.parent]);

	return (
		<div
			style={{
				display: 'flex',
				flexGrow: 1,
				height: '100%',
				overflowY: 'scroll',
			}}
		>
			{/* We need the 'span' for the react ref */}
			<span
				style={{
					maxHeight: '100%',
					maxWidth: '100%',
					flexGrow: 1,
					height: '100%',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						gap: '4px',
						justifyContent: 'space-between',
					}}
				>
					<span
						style={{
							maxWidth: '100%',
							maxHeight: '100%',
							overflowY: 'scroll',
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: metadataSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Name and description'
								subtitle={`Add a name and description for this ${READABLE_ENTITY_TYPES[
									entity.type
								].singular.toLowerCase()}`}
								hideShowSuffix='the name and description section'
								open={metadataSectionOpen}
								onToggle={() => setMetadataSectionOpen(!metadataSectionOpen)}
							/>

							<Collapse in={metadataSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 12,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<TextField
										label='Input name'
										variant='outlined'
										style={{ width: '50%' }}
										disabled={!entity.editable}
										size='small'
										value={entityName || ''}
										onChange={(e) => {
											setEntityName(e.target.value);
											debouncedOnChangeName(e.target.value);
										}}
									/>

									<TextField
										label='Input description'
										variant='outlined'
										disabled={!entity.editable}
										fullWidth
										multiline
										minRows={3}
										size='small'
										value={entityDescription || ''}
										onChange={(e) => {
											setEntityDescription(e.target.value);
											debouncedOnChangeDescription(e.target.value);
										}}
									/>
								</div>
							</Collapse>
						</section>

						{entity.type === EntityType.ArgumentDeclaration && (
							<>
								<hr
									style={{
										margin: '1px 0',
										border: '1px solid rgba(0,0,0,0.08)',
									}}
								/>

								<section
									style={{
										display: 'flex',
										flexDirection: 'column',
										paddingBottom: baseFunctionalitySectionOpen ? 20 : 0,
									}}
								>
									<EntityDialogSectionHeader
										title='Base functionality'
										subtitle='Link this input to functionality from an existing base input'
										hideShowSuffix='the base functionality section'
										open={baseFunctionalitySectionOpen}
										onToggle={() =>
											setBaseFunctionalitySectionOpen(
												!baseFunctionalitySectionOpen,
											)
										}
									/>

									<Collapse in={baseFunctionalitySectionOpen}>
										<div
											style={{
												marginTop: 6,
												display: 'flex',
												flexDirection: 'column',
												gap: 20,
												marginLeft: 80,
												paddingRight: 40,
											}}
										>
											<Autocomplete
												multiple
												disabled={!entity.editable}
												id='implements'
												options={implementsOptions}
												getOptionLabel={(option) => option.name}
												isOptionEqualToValue={(option, value) =>
													option.id === value.id
												}
												value={implementsValue}
												getOptionDisabled={(option) => {
													const baseParentOptions: ValueDescriptorState[] = [
														...(entity.parent.implements?.inputs || []),
													];

													let forcedOption = false;

													// If an option is not in the parent, and it is selected, it means it was added manually
													// We cannot let the user remove it
													if (
														!baseParentOptions.find((o) => o.id === option.id)
													) {
														forcedOption = true;
													}

													const alreadyImplemented =
														!!entity.parent.inputs.find(
															(p) =>
																p.id !== entity.id &&
																p.implements.find((i) => i.id === option.id),
														);

													return alreadyImplemented && !forcedOption;
												}}
												renderOption={(props, option) => {
													const { key, ...optionProps } = props;
													const ent = project.logic.get(option.id) as
														| PropertyState
														| ValueDescriptorState;

													const baseParentOptions: ValueDescriptorState[] = [
														...(entity.parent.implements?.inputs || []),
													];

													let forcedOption = false;

													// If an option is not in the parent, and it is selected, it means it was added manually
													// We cannot let the user remove it
													if (!baseParentOptions.find((o) => o.id === ent.id)) {
														forcedOption = true;
													}

													return (
														<MuiTooltip
															key={key}
															title={(() => {
																const alreadyImplemented =
																	!!entity.parent.inputs.find(
																		(p) =>
																			p.id !== entity.id &&
																			p.implements.find(
																				(i) => i.id === option.id,
																			),
																	);

																if (alreadyImplemented && !forcedOption) {
																	return 'This base input is already implemented by another input in the current node';
																}
															})()}
															placement='bottom'
															arrow
															enterDelay={
																(() => {
																	const alreadyImplemented =
																		!!entity.parent.inputs.find(
																			(p) =>
																				p.id !== entity.id &&
																				p.implements.find(
																					(i) => i.id === option.id,
																				),
																		);

																	return alreadyImplemented && !forcedOption;
																})()
																	? 100
																	: 400
															}
															leaveDelay={200}
														>
															<Box
																component='li'
																sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
																{...optionProps}
															>
																<Checkbox
																	icon={icon}
																	disabled={forcedOption}
																	checkedIcon={checkedIcon}
																	style={{ marginRight: 8 }}
																	checked={
																		implementsValue.findIndex(
																			(v) => v.id === ent.id,
																		) > -1
																	}
																/>
																<Stack
																	direction='row'
																	gap={1}
																	alignItems='center'
																>
																	<EntityIcon entity={ent} project={project} />
																	{resolveEntityName(ent, project.logic)}
																</Stack>
															</Box>
														</MuiTooltip>
													);
												}}
												renderTags={(selected) => (
													<Stack gap={1} direction='row' flexWrap='wrap'>
														{selected.map((value) => {
															const ent = project.logic.get(
																value.id,
															) as PropertyState;

															const baseParentOptions: ValueDescriptorState[] =
																[...(entity.parent.implements?.inputs || [])];

															let forcedOption = false;

															// If an option is not in the parent, and it is selected, it means it was added manually
															// We cannot let the user remove it
															if (
																!baseParentOptions.find((o) => o.id === ent.id)
															) {
																forcedOption = true;
															}

															return (
																<MuiTooltip
																	title={(() => {
																		if (forcedOption) {
																			return 'This base functionality was added by the system and cannot be removed';
																		}
																		return '';
																	})()}
																	placement='top'
																	arrow
																	enterDelay={100}
																	leaveDelay={200}
																>
																	<Box
																		component='span'
																		sx={{ display: 'inline-block' }}
																	>
																		<Chip
																			avatar={
																				<EntityIcon
																					entity={ent}
																					style={{ marginLeft: 8 }}
																					size='small'
																					project={project}
																				/>
																			}
																			key={value.id}
																			label={resolveEntityName(
																				ent,
																				project.logic,
																			)}
																			disabled={forcedOption}
																			onDelete={() => {
																				const changeSet =
																					project?.logic?.addChangeSet(
																						new ChangeSet(
																							project?.logic,
																							ProjectState.sessionAuthor,
																							new Date().toISOString(),
																							entity,
																							false,
																							StateMutationAction.RemoveArgBaseImplementation,
																						),
																					);

																				const fullEnt = project.logic.get(
																					value.id,
																				) as
																					| PropertyState
																					| ValueDescriptorState;

																				entity?.removeImplementation(
																					fullEnt,
																					changeSet,
																				);

																				changeSet.add(
																					entity,
																					ChangeSetEntityChangeType.Updated,
																				);

																				setImplementsValue(
																					implementsValue.filter(
																						(v) => v.id !== value.id,
																					),
																				);
																				project.emit(entity.id, {});

																				project.renderAndCloseChangeSet(
																					changeSet,
																				);
																			}}
																			deleteIcon={
																				<CancelIcon
																					onMouseDown={(event) =>
																						event.stopPropagation()
																					}
																				/>
																			}
																		/>
																	</Box>
																</MuiTooltip>
															);
														})}
													</Stack>
												)}
												onChange={(_, newValue) => {
													// Filter out duplicates
													const sanitizedValue = newValue.filter(
														(v, i, a) =>
															a.findIndex((t) => t.id === v.id) === i,
													);

													const baseParentOptions: ValueDescriptorState[] = [
														...(entity.parent.implements?.inputs || []),
													];

													const changeSet = project?.logic?.addChangeSet(
														new ChangeSet(
															project?.logic,
															ProjectState.sessionAuthor,
															new Date().toISOString(),
															entity,
															false,
															StateMutationAction.AddArgBaseImplementation,
														),
													);

													const existingEntities = entity.implements.map(
														(ent) => ent.id,
													);
													const newEntities = sanitizedValue.map(
														(ent) => ent.id,
													);

													const toAdd = newEntities.filter(
														(ent) => !existingEntities.includes(ent),
													);
													const toRemove = existingEntities.filter((ent) => {
														const currentEnt = project.logic.get(ent) as
															| PropertyState
															| ValueDescriptorState;
														if (
															(currentEnt.parent.type ===
																EntityType.DefinitionEntity ||
																currentEnt.parent.type ===
																	EntityType.BuiltInBaseEntity) &&
															!currentEnt.parent.additionalProperties
														) {
															return false;
														}

														// If an option is not in the parent, and it is selected, it means it was added manually
														// We cannot let the user remove it
														if (!baseParentOptions.find((o) => o.id === ent)) {
															return false;
														}

														if (!newEntities.includes(ent)) {
															return true;
														}
													});

													toAdd.forEach((ent) => {
														const fullEnt = project.logic.get(
															ent,
														) as PropertyState;
														entity?.addImplementation(fullEnt, changeSet);

														changeSet.add(
															entity,
															ChangeSetEntityChangeType.Updated,
														);
													});

													toRemove.forEach((ent) => {
														const fullEnt = project.logic.get(ent) as
															| PropertyState
															| ValueDescriptorState;
														entity?.removeImplementation(fullEnt, changeSet);

														changeSet.add(
															entity,
															ChangeSetEntityChangeType.Updated,
														);
													});

													if (toAdd.length || toRemove.length) {
														setImplementsValue(sanitizedValue);
														entity.afterAllChildrenInitialized(changeSet);
														project.emit(entity.id, {});
													}

													project.renderAndCloseChangeSet(changeSet);
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														label='Implements'
														variant='outlined'
														size='small'
													/>
												)}
											/>
										</div>
									</Collapse>
								</section>
							</>
						)}

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: dataTypeSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Data type'
								subtitle='Select the type of data that this input will hold'
								hideShowSuffix='the data type section'
								open={dataTypeSectionOpen}
								onToggle={() => setDataTypeSectionOpen(!dataTypeSectionOpen)}
								headerStartIcon={
									<EntityIcon entity={entity} project={project} />
								}
							/>

							<Collapse in={dataTypeSectionOpen}>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DataTypeBuilder
										project={project}
										dataType={dataType}
										topParent={entity}
										readOnly={!entity.editable}
										baseDataTypes={entity.implements.map(
											(impl) => impl.dataType,
										)}
										onChangeSet={() => {
											const newChangeSet = project.logic.addChangeSet(
												new ChangeSet(
													project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													StateMutationAction.ChangeArgDataType
												),
											);

											return newChangeSet;
										}}
										onChange={(newDataTypeEntity, changeSet) => {
											setDataType(newDataTypeEntity, changeSet);
										}}
										lists={
											resolveDataTypeAsListConfigForArgumentDeclaration(entity)
												.allowed
										}
										getOptionDisabled={(option) =>
											resolveDataTypeOptionConfigForArgumentDeclaration(
												option,
												entity,
											).disabled
										}
										getListDisabledTooltip={(option) =>
											resolveDataTypeAsListConfigForArgumentDeclaration(entity)
												.tooltip
										}
										getOptionTooltip={(option) =>
											resolveDataTypeOptionConfigForArgumentDeclaration(
												option,
												entity,
											).tooltip
										}
										disallowAsType={
											resolveDataTypeAsTypeConfigForArgumentDeclaration(entity)
												.disabled
										}
										getAsTypeDisabledTooltip={() =>
											resolveDataTypeAsTypeConfigForArgumentDeclaration(entity)
												.tooltip
										}
										disallowLike={
											resolveDataTypeLikeConfigForArgumentDeclaration(entity)
												.disabled
										}
										getLikeDisabledTooltip={() =>
											resolveDataTypeLikeConfigForArgumentDeclaration(entity)
												.tooltip
										}
									/>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: defaultValueSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Default value'
								subtitle='The initial value, if no other is given when the input is first used'
								hideShowSuffix='the default value section'
								open={defaultValueSectionOpen}
								onToggle={() =>
									setDefaultValueSectionOpen(!defaultValueSectionOpen)
								}
								headerStartIcon={
									!!entity.errors.find(
										(error) =>
											error.code ===
											EntityInstanceErrorCode.MissingRequiredValue,
									) ? (
										<MuiTooltip
											title='Missing required value'
											placement='top'
											arrow
										>
											<img
												style={{ width: 20, height: 20 }}
												id={'logic-error-icon--' + entity.id}
												src={WarningIcon}
												className={'warning-icon logic-error-icon'}
											/>
										</MuiTooltip>
									) : null
								}
							/>

							<Collapse in={defaultValueSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DynamicValueEntry
										entity={entity}
										project={project}
										parentDisabledReasons={
											!entity.editable
												? [StateMutationErrorCode.ActionOwnerEntityNotEditable]
												: []
										}
										onlyDefault
									>
										{(extraContent) => extraContent}
									</DynamicValueEntry>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: interactivitySectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Interactivity'
								subtitle='Define how this input is used in the logic'
								hideShowSuffix='the interactivity section'
								open={interactivitySectionOpen}
								onToggle={() =>
									setInteractivitySectionOpen(!interactivitySectionOpen)
								}
							/>

							<Collapse in={interactivitySectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'row',
										gap: 12,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<MuiTooltip
										title={
											'When checked, the value of this input must be present in any representation of the parent definition-entity as a value'
										}
										placement='bottom'
										arrow
										enterDelay={false ? 100 : 400}
										leaveDelay={200}
									>
										<CheckboxWithLabel
											mainLabel="Required"
											subLabel={isRequired ? "A value must be provided" : "A value is optional"}
											disabled={!entity.editable}
											className='input-declaration-builder--required-checkbox'
											checked={isRequired}
											onChange={(checked) => {
												setIsRequired(checked as boolean);
											}}
										/>
									</MuiTooltip>
								</div>
							</Collapse>
						</section>
					</span>

					{errors.length > 0 && (
						<div
							style={{
								marginTop: 20,
								marginRight: 40,
								marginLeft: 80,
							}}
						>
							<Alert severity='error'>
								{errors.length === 1 ? (
									<span
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 6,
										}}
									>
										<h4
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											Error:
										</h4>
										<p
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											{fromArgumentDeclarationErrorToReadableMessage(errors[0])}
										</p>
									</span>
								) : (
									<>
										<h4
											style={{
												margin: 0,
											}}
										>
											Errors
										</h4>
										<br className='quarter-space' />
										<ul
											style={{
												listStyleType: 'none',
												padding: 0,
											}}
										>
											{errors.map((error, index) => (
												<li
													key={index}
													style={{
														display: 'flex',
														alignItems: 'center',
													}}
												>
													{/* Circle */}
													<span
														style={{
															fontSize: 10,
															marginRight: 5,
														}}
													>
														&#9679;
													</span>
													<p
														style={{
															margin: 0,
															// fontSize: 10,
														}}
													>
														{fromArgumentDeclarationErrorToReadableMessage(
															error,
														)}
													</p>
												</li>
											))}
										</ul>
									</>
								)}
							</Alert>
						</div>
					)}

					<div
						style={{
							alignSelf: 'flex-start',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							width: '100%',
							gap: '16px',
							padding: '0 40px',
						}}
					>
						<Button
							id='input-declaration--done-button'
							onClick={(e: any) => {
								onClose?.(e);
							}}
							color='primary'
							disabled={errors.length > 0}
						>
							Done
						</Button>
						<Tooltip
							anchorQuerySelector='#input-declaration--done-button'
							placement='bottom'
							openDelay={100}
						>
							{errors.length > 0
								? 'Resolve the errors to save the input definition'
								: 'Save the input definition'}
						</Tooltip>
					</div>
				</div>
			</span>
		</div>
	);
};

export default ArgumentDeclaration;
